import { css } from "styled-system/css";

/**
 * Renders subtle shadow overlays at the left/right edges of a horizontally
 * scrollable region so overflow is more discoverable. Shadow visibility is
 * driven by scroll position via CSS scroll-driven animations: the left shadow
 * appears only after scrolling right, and the right shadow disappears once
 * the user reaches the end. When content fits without overflowing, no shadows
 * render.
 *
 * Two-part API — the split lets the scroller have padding while shadows still
 * sit at the wrapper's true edges:
 * - `horizontalOverflowShadowContainerStyles` — apply to a non-scrolling
 *   wrapper element. It owns the shadow pseudo-elements and provides a
 *   `timeline-scope` so they can reference the scroller's named timeline.
 * - `horizontalOverflowShadowScrollerStyles` — apply to the scrollable child
 *   element (which already has `overflow-x: auto | scroll`). It defines the
 *   named scroll timeline that the shadows animate against.
 *
 * Usage:
 * ```tsx
 * <div className={horizontalOverflowShadowContainerStyles}>
 *   <div className={cx(myScrollerStyles, horizontalOverflowShadowScrollerStyles)}>
 *     {children}
 *   </div>
 * </div>
 * ```
 *
 * The entire animation setup lives under `@supports (scroll-timeline: --x)`.
 * Browsers without scroll-timeline support don't get the shadow at all —
 * important because otherwise `animation-timeline: --x` would be ignored,
 * the animation would fall back to time-based (0s duration), and with
 * `animation-fill-mode: both` the fade-in would immediately stick at
 * opacity 1, showing a permanent shadow with no overflow context.
 *
 * Browser support: Chrome/Edge 115+, Firefox 140+, Safari 26+. In unsupported
 * browsers the shadows are absent — content still scrolls normally.
 *
 * Do not compose the container styles onto the same element as
 * `insetFocusRing`/`focusRing` — both use `::before`, so a focused element
 * would render the focus ring on top of the shadow. Put focus-ring styles on
 * a sibling wrapper instead.
 */
export const horizontalOverflowShadowContainerStyles = css({
  position: "relative",
  overflow: "hidden",
  "@supports (animation-timeline: --x)": {
    timelineScope: "[--overflow-shadow-scroll]",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "0",
      bottom: "0",
      w: "24",
      pointerEvents: "none",
      opacity: 0,
      zIndex: "[1]",
      animationTimeline: "[--overflow-shadow-scroll]",
      animationFillMode: "both",
    },
    "&::before": {
      left: "0",
      background: "[linear-gradient(to right, {colors.dark.700/60}, transparent)]",
      animationName: "fade-in",
      animationRangeStart: "[0]",
      animationRangeEnd: "[24px]",
    },
    "&::after": {
      right: "0",
      background: "[linear-gradient(to left, {colors.dark.700/60}, transparent)]",
      animationName: "fade-out",
      animationRangeStart: "[calc(100% - 24px)]",
      animationRangeEnd: "[100%]",
    },
  },
});

export const horizontalOverflowShadowScrollerStyles = css({
  scrollTimeline: "[--overflow-shadow-scroll inline]",
});
